import React from 'react';
import { Typography, Box, SxProps, Theme } from '@mui/material';
import {
    Person as PlayerIcon,
    AttachMoney as MoneyIcon,
    Timeline as RoundIcon,
    Stars as HandIcon,
    Input as JoinIcon,
    Output as LeaveIcon,
    Warning as SystemIcon,
    Update as UpdateIcon,
} from '@mui/icons-material';
import CardImage from '../Components/GameRoom/SubComponents/Card';
import { t } from 'i18next';

export type MessageType =
    | 'playerJoin'
    | 'playerJoinNextHand'
    | 'playerLeave'
    | 'playerAction'
    | 'winners'
    // | 'showCards'
    | 'balanceUpdate'
    | 'excessChipsReturn'
    | 'stateChange'
    | 'systemMessage'
    | 'gameStarting'
    | 'antePosted'
    | 'finalPot'
    | 'blindPost'

const STYLES = {
    baseContainer: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: '8px',
        padding: '8px 12px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        alignItems: 'start',
        width: '100%',
    } as SxProps<Theme>,

    timeContainer: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexShrink: 0,
    } as SxProps<Theme>,

    time: {
        fontFamily: '"Inter", serif',
        color: '#66666b',
        fontSize: '12px',
        flexShrink: 0,
    } as SxProps<Theme>,

    content: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
        minWidth: 0,
    } as SxProps<Theme>,

    username: {
        fontSize: '13px',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        color: 'white',
        fontWeight: 500,
        flexShrink: 0,
    } as SxProps<Theme>,

    message: {
        fontSize: '13px',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        color: 'white',
        opacity: 0.7,
        whiteSpace: 'normal',
    } as SxProps<Theme>,

    amount: {
        fontSize: '13px',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        color: '#3de361',
        fontWeight: 500,
        flexShrink: 0,
    } as SxProps<Theme>,
} as const;

export interface WinnerData {
    username: string;
    amount: number;
    handRank: string;
    cards: string[];
}

export interface ActivityMessageData {
    timestamp: string;
    username?: string;
    action?: string;
    amount?: number;
    currency?: string;
    state?: string;
    message?: string;
    reason?: string;
    seconds?: number;
    newBalance?: number;
    cards?: string[];
    handRank?: string;
    winners?: WinnerData[];
    communityCards?: string[];
    showdownType?: string;
    [key: string]: unknown;
}

export interface ActivityMessage {
    template: MessageType;
    data: ActivityMessageData;
    messageKey?: string;
    params?: Record<string, unknown>;
}

const COLORS = [
    '#19e5e0',  // cyan
    '#f6c944',  // gold
    '#ff9f64',  // orange
    '#9147ff',  // purple
    '#3de361',  // green
    '#ff64b1',  // pink
    '#64d2ff',  // light blue
    '#ff6464',  // coral
] as const;

export class TemplateUtil {
    private static translateText(key: string, options?: Record<string, unknown>): string {
        return String(t(key, options));
    }

    private static generateColorFromString(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        return COLORS[Math.abs(hash) % COLORS.length];
    }

    private static formatMoney(amount: number, currency = 'USD'): string {
        if(currency === 'chips') {
            return `${amount}`;
        }
        // Normalize currency code to uppercase
        const normalizedCurrency = currency?.toUpperCase() || 'USD';

        // Crypto currencies
        const cryptoCurrencies = {
            'DOGE': { decimals: 8, symbol: 'Ð' },
            'SOL': { decimals: 9, symbol: 'SOL' },
            'SOLAMA': { decimals: 9, symbol: 'SOLAMA' },
            'DOT': { decimals: 10, symbol: 'DOT' },
            'BNB': { decimals: 8, symbol: 'BNB' },
            'XMR': { decimals: 12, symbol: 'XMR' },
            'SHIB': { decimals: 18, symbol: 'SHIB' },
            'VLX': { decimals: 18, symbol: 'VLX' },
            'USDT': { decimals: 6, symbol: 'USDT' },
            'USDC': { decimals: 6, symbol: 'USDC' },
            'BUSD': { decimals: 18, symbol: 'BUSD' },
            'DAI': { decimals: 18, symbol: 'DAI' }
        };

        // Check if it's a crypto currency
        if (cryptoCurrencies[normalizedCurrency as keyof typeof cryptoCurrencies]) {
            const { decimals, symbol } = cryptoCurrencies[normalizedCurrency as keyof typeof cryptoCurrencies];
            const formattedAmount = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
            }).format(amount);
            
            return `${formattedAmount} ${symbol}`;
        }

        // For fiat currencies, use standard currency formatting
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: normalizedCurrency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(amount);
        } catch (error) {
            // Fallback formatting if currency code is not supported
            console.warn(`Unsupported currency code: ${normalizedCurrency}, falling back to basic formatting`);
            return `${amount.toFixed(2)} ${normalizedCurrency}`;
        }
    }

    private static formatTime(isoTimestamp: string): string {
        const date = new Date(isoTimestamp);
        return date.toLocaleTimeString('en-US', {
            hour12: true,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    private static getTemplateIcon(template: MessageType): React.ReactElement {
        const iconStyle = { fontSize: 16 };
        
        switch (template) {
            case 'playerJoin':
            case 'playerJoinNextHand':
                return <JoinIcon sx={{ ...iconStyle, color: '#3de361' }} />;
            case 'playerLeave':
                return <LeaveIcon sx={{ ...iconStyle, color: '#f52740' }} />;
            case 'playerAction':
                return <PlayerIcon sx={{ ...iconStyle, color: '#19e5e0' }} />;
            case 'winners':
                return <HandIcon sx={{ ...iconStyle, color: '#f6c944' }} />;
            // case 'showCards':
            //     return <DealerIcon sx={{ ...iconStyle, color: '#9147ff' }} />;
            case 'balanceUpdate':
            case 'excessChipsReturn':
                return <MoneyIcon sx={{ ...iconStyle, color: '#3de361' }} />;
            case 'stateChange':
                return <RoundIcon sx={{ ...iconStyle, color: '#64d2ff' }} />;
            case 'systemMessage':
                return <SystemIcon sx={{ ...iconStyle, color: '#ff6464' }} />;
            case 'gameStarting':
                return <UpdateIcon sx={{ ...iconStyle, color: '#3de361' }} />;
            case 'antePosted':
            case 'finalPot':
                return <MoneyIcon sx={{ ...iconStyle, color: '#3de361' }} />;
            case 'blindPost':
                return <MoneyIcon sx={{ ...iconStyle, color: '#3de361' }} />;
            default:
                return <UpdateIcon sx={{ ...iconStyle, opacity: 0.5 }} />;
        }
    }

    public static renderActivityMessage(message: ActivityMessage): React.ReactElement | null {
        if (!message.data?.timestamp) {
            console.warn('Invalid message format - missing timestamp:', message);
            return null;
        }

        const { template, data } = message;
        const time = this.formatTime(data.timestamp);

        switch (template) {
            case 'playerJoin':
            case 'playerJoinNextHand': {
                const translationKey = template === 'playerJoin'
                    ? 'Activity:activity.playerJoin'
                    : 'Activity:activity.playerJoinNextHand';
                const translatedMessage = this.translateText(translationKey, { username: data.username });
                const parts = translatedMessage.split(data.username || '');

                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {parts[0] && <Typography sx={STYLES.message}>{parts[0]}</Typography>}
                            <Typography
                                sx={{
                                    ...STYLES.username,
                                    color: this.generateColorFromString(data.username ?? '')
                                }}
                            >
                                {data.username}
                            </Typography>
                            {parts[1] && <Typography sx={STYLES.message}>{parts[1]}</Typography>}
                        </Box>
                    </Box>
                );
            }

            case 'playerAction': {
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            <Typography 
                                sx={{ 
                                    ...STYLES.username, 
                                    color: this.generateColorFromString(data.username ?? '') 
                                }}
                            >
                                {data.username}
                            </Typography>
                            <Typography sx={STYLES.message}>{data.action}</Typography>
                            {data.amount !== undefined && (
                                <Typography sx={STYLES.amount}>
                                    {this.formatMoney(data.amount, data.currency)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }

            case 'winners': {
                const winnerLabel = this.translateText('Activity:activity.winnerLabel', { defaultValue: 'Winner:' });
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {data.winners?.map((winner, index) => (
                                <Box sx={STYLES.content} key={`winner-${index}`}>
                                    <Typography
                                        sx={{
                                            ...STYLES.message,
                                            color: '#ffd700'
                                        }}
                                    >
                                        {winnerLabel}
                                    </Typography>
                                    <Typography
                                        sx={{ ...STYLES.username, color: this.generateColorFromString(winner.username) }}
                                    >
                                        {winner.username}
                                    </Typography>
                                    <Typography sx={STYLES.amount}>
                                        {this.formatMoney(winner.amount, data.currency)}
                                    </Typography>

                                    {winner.handRank && (
                                        <>
                                            <Typography sx={STYLES.message}>
                                                {` - `}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        fontSize: '0.8em'
                                                    }}
                                                >
                                                    {winner.handRank.split(' ').join(' ')}
                                                </Typography>
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center', ml: 1 }}>
                                                {data.communityCards?.map((card, cardIndex) => (
                                                    <CardImage
                                                        key={`community-${card}-${cardIndex}`}
                                                        card={card}
                                                        extraSmall={true}
                                                        style={{ marginRight: '2px' }}
                                                    />
                                                ))}
                                                {winner.cards && winner.cards.length > 0 && (
                                                    <Typography sx={{ ...STYLES.message, mx: 1 }}>+</Typography>
                                                )}
                                                {winner.cards.map((card, cardIndex) => (
                                                    <CardImage
                                                        key={`${card}-${cardIndex}`}
                                                        card={card}
                                                        extraSmall={true}
                                                        style={{ marginRight: '2px' }}
                                                    />
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                </Box>

                            ))}
                        </Box>
                    </Box>
                );
            }

            // case 'showCards': {
            //     return (
            //         <Box sx={STYLES.baseContainer}>
            //             <Box sx={STYLES.timeContainer}>
            //                 {this.getTemplateIcon(template)}
            //                 <Typography sx={STYLES.time}>{time}</Typography>
            //             </Box>
            //             <Box sx={STYLES.content}>
            //                 <Typography sx={{ ...STYLES.username, color: this.generateColorFromString(data.username || '') }}>
            //                     {data.username}
            //                 </Typography>
            //                 <Typography sx={STYLES.message}>
            //                     shows [{data.cards?.join(', ')}]
            //                 </Typography>
            //             </Box>
            //         </Box>
            //     );
            // }

            case 'stateChange': {
                const stateKey = data.state ? `Activity:activity.states.${data.state}` : '';
                const translatedState = stateKey
                    ? this.translateText(stateKey, { defaultValue: data.state?.replace(/([A-Z])/g, ' $1').trim() })
                    : '';
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            <Typography sx={STYLES.message}>
                                {translatedState}
                            </Typography>
                        </Box>
                    </Box>
                );
            }

            case 'systemMessage': {
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            <Typography sx={STYLES.message}>
                                {data.message || ''}
                            </Typography>
                        </Box>
                    </Box>
                );
            }

            case 'balanceUpdate': {
                if (!data.username) {
                    console.warn('Missing username in balanceUpdate message:', data);
                    return null;
                }

                const balanceMessage = this.translateText('Activity:activity.balanceUpdate', {
                    username: data.username,
                    newBalance: this.formatMoney(data.newBalance || 0, data.currency),
                    currency: data.currency || ''
                });
                const parts = balanceMessage.split(data.username || '');

                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {parts[0] && <Typography sx={STYLES.message}>{parts[0]}</Typography>}
                            <Typography
                                sx={{
                                    ...STYLES.username,
                                    color: this.generateColorFromString(data.username)
                                }}
                            >
                                {data.username}
                            </Typography>
                            {parts[1] && <Typography sx={STYLES.message}>{parts[1]}</Typography>}
                        </Box>
                    </Box>
                );
            }

            case 'gameStarting': {
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            <Typography sx={STYLES.message}>
                                {this.translateText('Activity:activity.gameStarting', { seconds: data.seconds })}
                            </Typography>
                        </Box>
                    </Box>
                );
            }

            case 'excessChipsReturn': {
                const translationKey = data.reason === 'smaller_stack'
                    ? 'Activity:activity.excessReturnSmallerStack'
                    : 'Activity:activity.excessReturnInsufficientPlayers';
                const translatedMessage = this.translateText(translationKey, {
                    amount: this.formatMoney(data.amount || 0, data.currency),
                    currency: data.currency || '',
                    username: data.username
                });
                const parts = translatedMessage.split(data.username || '');

                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {parts[0] && <Typography sx={STYLES.message}>{parts[0]}</Typography>}
                            <Typography sx={{ ...STYLES.username, color: this.generateColorFromString(data.username || '') }}>
                                {data.username}
                            </Typography>
                            {parts[1] && <Typography sx={STYLES.message}>{parts[1]}</Typography>}
                        </Box>
                    </Box>
                );
            }

            case 'antePosted': {
                const translatedMessage = this.translateText('Activity:activity.antePosted', {
                    username: data.username,
                    amount: this.formatMoney(data.amount || 0, data.currency),
                    currency: data.currency || ''
                });
                const parts = translatedMessage.split(data.username || '');

                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {parts[0] && <Typography sx={STYLES.message}>{parts[0]}</Typography>}
                            <Typography
                                sx={{
                                    ...STYLES.username,
                                    color: this.generateColorFromString(data.username || '')
                                }}
                            >
                                {data.username}
                            </Typography>
                            {parts[1] && <Typography sx={STYLES.message}>{parts[1]}</Typography>}
                        </Box>
                    </Box>
                );
            }

            case 'finalPot': {
                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            <Typography sx={STYLES.message}>
                                {this.translateText('Activity:activity.potFinal', {
                                    amount: this.formatMoney(data.amount || 0, data.currency),
                                    currency: data.currency || ''
                                })}
                            </Typography>
                        </Box>
                    </Box>
                );
            }

            case 'blindPost': {
                const blindTypeKey = data.blindType === 'small blind'
                    ? 'Activity:activity.blindSmall'
                    : data.blindType === 'big blind'
                        ? 'Activity:activity.blindBig'
                        : 'Activity:activity.blindPost';
                const translatedMessage = this.translateText(blindTypeKey, {
                    username: data.username,
                    blindType: data.blindType || 'blinds',
                    amount: this.formatMoney(data.amount || 0, data.currency),
                    currency: data.currency || ''
                });
                const parts = translatedMessage.split(data.username || '');

                return (
                    <Box sx={STYLES.baseContainer}>
                        <Box sx={STYLES.timeContainer}>
                            {this.getTemplateIcon(template)}
                            <Typography sx={STYLES.time}>{time}</Typography>
                        </Box>
                        <Box sx={STYLES.content}>
                            {parts[0] && <Typography sx={STYLES.message}>{parts[0]}</Typography>}
                            <Typography
                                sx={{
                                    ...STYLES.username,
                                    color: this.generateColorFromString(data.username || '')
                                }}
                            >
                                {data.username}
                            </Typography>
                            {parts[1] && <Typography sx={STYLES.message}>{parts[1]}</Typography>}
                        </Box>
                    </Box>
                );
            }

            default:
                return this.renderInvalidMessage();
        }
    }

    private static renderInvalidMessage(): React.ReactElement | null {
        return null;
    }
}
